// src/components/management/Chat/ChatFilters.jsx
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "../../../styles/components/management/Chat/ChatFilters.css";

const SESSION_KEY = "chatFilters";

export default function ChatFilters({
  bookings,
  setFilteredBookings,
  iconRef,
  collapsed,
  setCollapsed,
  unreadMap,
}) {
  const [filters, setFilters] = useState(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    return stored
      ? JSON.parse(stored)
      : {
          unreadOnly: false,
          platform: "",
          search: "",
          status: "",
          checkIn: "",
          checkOut: "",
          property: "",
        };
  });

  const filterRef = useRef(null);
  const containerRef = useRef(null);
  const [resultCount, setResultCount] = useState(0);

  useEffect(() => {
    const node = document.createElement("div");
    node.className = "chat-filters-portal";
    document.body.appendChild(node);
    containerRef.current = node;
    return () => {
      document.body.removeChild(node);
    };
  }, []);

  useEffect(() => {
    let filtered = bookings.filter((b) => {
      if (filters.unreadOnly && (!unreadMap[b.ID] || unreadMap[b.ID] <= 0))
        return false;
      if (filters.platform && b.PLATFORM !== filters.platform) return false;
      if (filters.status && b.STATUS !== filters.status) return false;
      if (
        filters.search &&
        !`${b.GUEST_NAME} ${b.ID}`
          .toLowerCase()
          .includes(filters.search.toLowerCase())
      )
        return false;
      if (filters.checkIn && b.CHECK_IN_DATE < filters.checkIn) return false;
      if (filters.checkOut && b.CHECK_OUT_DATE > filters.checkOut) return false;
      if (filters.property && b.PROPERTY_NAME !== filters.property)
        return false;
      return true;
    });
    setFilteredBookings(filtered);
    setResultCount(filtered.length);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(filters));
  }, [filters, bookings, setFilteredBookings, unreadMap]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(e.target) &&
        (!iconRef?.current || !iconRef.current.contains(e.target))
      ) {
        setCollapsed(true);
      }
    };
    if (!collapsed) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [collapsed, iconRef, setCollapsed]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetFilters = () => {
    const cleared = {
      unreadOnly: false,
      platform: "",
      search: "",
      status: "",
      checkIn: "",
      checkOut: "",
      property: "",
    };
    setFilters(cleared);
    sessionStorage.removeItem(SESSION_KEY);
  };

  const uniqueStatuses = [
    ...new Set(bookings.map((b) => b.STATUS).filter(Boolean)),
  ];
  const uniqueProperties = [
    ...new Set(bookings.map((b) => b.PROPERTY_NAME).filter(Boolean)),
  ];
  const uniquePlatforms = [
    ...new Set(bookings.map((b) => b.PLATFORM).filter(Boolean)),
  ];

  if (!containerRef.current) return null;

  return createPortal(
    <div
      className={`chat-filters ${collapsed ? "collapsed" : "fade-in"}`}
      ref={filterRef}
      style={{ top: "80px" }}
    >
      {!collapsed && (
        <div className="chat-filters-contents">
          <div className="filter-group">
            <label>
              <input
                type="checkbox"
                name="unreadOnly"
                checked={filters.unreadOnly}
                onChange={handleChange}
              />
              Unread Only
            </label>

            <label>Platform</label>
            <select
              name="platform"
              value={filters.platform}
              onChange={handleChange}
            >
              <option value="">All Platforms</option>
              {uniquePlatforms.map((p) => (
                <option key={p} value={p}>
                  {p === "Booking.com" && "📘 "}
                  {p === "Airbnb" && "🏡 "}
                  {p === "zan.investments.bnb" && "🌐 "}
                  {p}
                </option>
              ))}
            </select>

            <label>Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleChange}
              placeholder="Guest name or ID"
            />
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleChange}
            >
              <option value="">All Statuses</option>
              {uniqueStatuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <label>Check-in</label>
            <input
              type="date"
              name="checkIn"
              value={filters.checkIn}
              onChange={handleChange}
            />

            <label>Check-out</label>
            <input
              type="date"
              name="checkOut"
              value={filters.checkOut}
              onChange={handleChange}
            />
          </div>

          <div className="filter-group">
            <label>Property</label>
            <select
              name="property"
              value={filters.property}
              onChange={handleChange}
            >
              <option value="">All Properties</option>
              {uniqueProperties.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>

            <div className="filter-footer">
              <button className="reset-button" onClick={resetFilters}>
                Reset
              </button>
              <span
                className="result-count"
                onClick={() => setCollapsed(true)}
                style={{ cursor: "pointer" }}
              >
                {resultCount} results
              </span>
            </div>
          </div>
        </div>
      )}
    </div>,
    containerRef.current
  );
}
